import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Whether an element's text is currently clipped — the test for "does this need a tooltip?" on any
 * single-line label styled with `text-overflow: ellipsis`.
 *
 * Attach the returned `ref` to the element that carries the `overflow: hidden` (the one the browser
 * ellipsizes), and pass the text as `value` so a new label re-measures. Resizes are picked up through
 * a `ResizeObserver` on the element, with a `window` resize listener as the fallback where the
 * observer is missing.
 */
export function useIsTruncated<T extends HTMLElement = HTMLElement>(value?: unknown) {
  const ref = useRef<T | null>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  const measure = useCallback(() => {
    const el = ref.current
    if (!el) {
      return
    }
    // The 1px slack absorbs sub-pixel rounding: a fractional text width can report `scrollWidth` a
    // pixel past `clientWidth` with nothing actually clipped, which would show a pointless tooltip.
    setIsTruncated(el.scrollWidth > el.clientWidth + 1)
  }, [])

  useEffect(() => {
    measure()

    const el = ref.current
    if (el && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => measure())
      observer.observe(el)
      return () => observer.disconnect()
    }

    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [measure, value])

  return { ref, isTruncated }
}
